import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_KEY = 'ott_default_page';

export type DefaultPage = 'home' | 'live-tv-categories' | 'movies' | 'series' | 'kids' | 'anime' | 'sports';

export function getDefaultPage(): DefaultPage {
  return (localStorage.getItem(DEFAULT_PAGE_KEY) as DefaultPage) ?? 'home';
}

interface InterfaceSectionProps {
  focused: boolean;
}

const OPTIONS: { value: DefaultPage; labelKey: string }[] = [
  { value: 'home',               labelKey: 'settings.interface.pageTv' },
  { value: 'sports',             labelKey: 'settings.interface.pageSports' },
  { value: 'movies',             labelKey: 'settings.interface.pageMovies' },
  { value: 'series',             labelKey: 'settings.interface.pageSeries' },
  { value: 'kids',               labelKey: 'settings.interface.pageKids' },
  { value: 'anime',              labelKey: 'settings.interface.pageAnime' },
];

const InterfaceSection: React.FC<InterfaceSectionProps> = ({ focused }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<DefaultPage>(getDefaultPage);
  const firstRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused) firstRef.current?.focus();
  }, [focused]);

  const handleSelect = (value: DefaultPage) => {
    setSelected(value);
    localStorage.setItem(DEFAULT_PAGE_KEY, value);
  };

  return (
    <div className="settings-section">
      <h2 className="settings-section__title">{t('settings.interface.title')}</h2>

      <div className="settings-card">
        <div className="settings-row__label" style={{ marginBottom: 8 }}>
          {t('settings.interface.defaultPage')}
        </div>

        {OPTIONS.map((opt, idx) => {
          const isSelected = selected === opt.value;
          return (
            <div
              key={opt.value}
              ref={idx === 0 ? firstRef : undefined}
              className={['settings-radio-row', isSelected ? 'settings-radio-row--selected' : ''].filter(Boolean).join(' ')}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => handleSelect(opt.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(opt.value);
                }
              }}
            >
              <div className={`settings-radio-dot${isSelected ? ' settings-radio-dot--selected' : ''}`}>
                {isSelected && <div className="settings-radio-dot__inner" />}
              </div>
              <div className="settings-radio-label">
                <p className="settings-radio-label__title">{t(opt.labelKey)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InterfaceSection;
